import { Reporter } from 'gatsby';
import { UserType } from '../../types/user-type';
import { Alternate } from '../../types/alternate';
import { Domain } from '../../types/domain';
import { StoryblokAlternate } from '../../types/storyblok-alternate';
import { StoryblokClientService } from '../storyblok-client';
import { getGatsbySourceGraphqlConfig } from '../../utils/get-gatsby-source-graphql-config';
import { StringService } from '../string';
import { GlobalContent } from '../../types/global-content';
import { Homepage } from '../../types/homepage';
import { Country, CountryEnum } from '../../types/country';
import { DomainService } from '../domain';
import { LocaleEnum } from '../../types/locale';

export type StoryblokDatasource = {
  id: string;
  name: string;
  slug: string;
  dimensions: {
    id: string;
    entry_value: string;
    name: string;
  }[];
};

export type StoryblokDatasourceEntry = {
  id: string;
  name: string;
  value: string;
  dimension_value: string;
};

const deepen = (obj): { [key: string]: string } => {
  const result = {};
  Object.keys(obj).forEach((key) => {
    const parts = key.split('.');
    let target = result;
    while (parts.length > 1) {
      const part = parts.shift();
      target[part] = target[part] || {};
      target = target[part] || {};
    }
    target[parts[0]] = obj[key];
  });

  return result;
};

const getUrlParams = (): Record<string, string | true> =>
  window.location.search
    .substr(1)
    .split('&')
    .filter((slug) => !!slug)
    .reduce(
      (accumulator, slug) => ({
        ...accumulator,
        ...(slug.split('=')[0] && {
          [`$${slug.split('=')[0]}`]: slug.split('=')[1] || true,
        }),
      }),
      {},
    );

export const StoryblokService = {
  // moved from gatsby-node.js
  async getAllDatasourceEntries(): Promise<Record<string, GlobalContent>> {
    const storyblokDatasources = await StoryblokClientService.getAllDatasources();

    const storyblokDatasourceDimensions = storyblokDatasources
      .map((datasource) => datasource.dimensions.map((dimension) => dimension.entry_value))
      .flat()
      .filter((dimension, index, allDimensions) => allDimensions.indexOf(dimension) === index);

    const defaultDatasourceEntries = await StoryblokClientService.getDefaultDatasourceEntries();

    const storyblokDatasourceEntriesPromises = storyblokDatasourceDimensions.map(async (dimension) =>
      StoryblokClientService.getDatasourceEntriesByDimension(dimension),
    );

    const storyblokDatasourceEntries = await Promise.all(storyblokDatasourceEntriesPromises);

    return this.getGlobalDatasourceEntries({
      datasourceEntries: storyblokDatasourceEntries,
      dimensions: storyblokDatasourceDimensions,
      defaultValue: defaultDatasourceEntries,
    });
  },

  getConfig(): any {
    return getGatsbySourceGraphqlConfig();
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getObject(): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { storyblok } = window as any;
    return storyblok || undefined;
  },

  getObjectV2(): any {
    const { StoryblokBridge } = window as any;
    return new StoryblokBridge({
      resolveRelations: this.getConfig().options.resolveRelations.split(','),
    });
  },

  getLocalizedDatasourceEntries(localizeDatasourceEntries: {
    datasourceEntries: StoryblokDatasourceEntry[][];
    dimensions: string[];
    countryCode: string;
    defaultValue: StoryblokDatasourceEntry[];
  }): StoryblokDatasourceEntry[] {
    const { datasourceEntries, dimensions, countryCode, defaultValue } = localizeDatasourceEntries;
    const countryCodeIndex = dimensions.indexOf(countryCode);

    if (countryCodeIndex === -1) {
      return defaultValue;
    }
    if (datasourceEntries[countryCodeIndex]) {
      return datasourceEntries[countryCodeIndex];
    }
    return defaultValue;
  },

  parseDatasourceEntries(datasourceEntries): GlobalContent {
    const datasourceValues = datasourceEntries.reduce(
      (object, item) => {
        if (item.dimension_value) {
          return {
            ...object,
            // empty datasource returns "''"
            [item.name]: item.dimension_value?.replace(/^''$/, ''),
          };
        }

        return {
          ...object,
          // empty datasource returns "''"
          [item.name]: item.value?.replace(/^''$/, ''),
        };
      },
      {} as { [key: string]: string },
    );
    return deepen(datasourceValues) as unknown as GlobalContent;
  },

  // todo masha: all datasource functionality has to be in a separate service
  getGlobalDatasourceEntries({
    dimensions,
    datasourceEntries,
    defaultValue,
  }: {
    dimensions: string[];
    datasourceEntries: StoryblokDatasourceEntry[][];
    defaultValue: StoryblokDatasourceEntry[];
  }): Record<string, GlobalContent> {
    return dimensions.reduce<Record<string, GlobalContent>>((_acc: Record<string, GlobalContent>, dimension: string) => {
      const localisedDatasourceEntries = this.getLocalizedDatasourceEntries({
        dimensions,
        datasourceEntries,
        defaultValue,
        countryCode: dimension,
      });

      return {
        ..._acc,
        [dimension]: this.parseDatasourceEntries(localisedDatasourceEntries),
      };
    }, {});
  },

  getPageUserType(
    fullSlug: string,
    domain: Domain,
    country: Country,
    localizedUserTypeSlug: { retail: string; institutional: string },
  ): UserType | null {
    if (!DomainService.isRebranded(country)) {
      return this.getUserTypeFromSlug(fullSlug, localizedUserTypeSlug);
    }

    if (DomainService.isRetailDomain(domain)) {
      return UserType.RETAIL;
    }

    if (DomainService.isInstitutionalDomain(domain)) {
      return UserType.INSTITUTIONAL;
    }

    return null;
  },

  getUserTypeFromSlug(fullSlug: string, localizedUserTypeSlug: { retail: string; institutional: string }): UserType | null {
    const { retail: retailSlug, institutional: institutionalSlug } = localizedUserTypeSlug;

    // Break the fullSlug into parts by slashes
    const slugParts = fullSlug.split('/');

    // Check if any part matches the retail slug
    if (slugParts.includes(retailSlug)) {
      return UserType.RETAIL;
    }

    // Check if any part matches the institutional slug
    if (slugParts.includes(institutionalSlug)) {
      return UserType.INSTITUTIONAL;
    }

    // If no match, return null
    return null;
  },

  loadStoryblokBridge(callback: any) {
    const existingScript = document.getElementById('storyblokBridge');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = '//app.storyblok.com/f/storyblok-v2-latest.js';
      script.id = 'storyblokBridge';
      document.body.appendChild(script);
      script.onload = () => {
        callback();
      };
    } else {
      callback();
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async redirect(callback?: (...args: any[]) => void): Promise<void> {
    // eslint-disable-next-line compat/compat
    return new Promise((resolve) => {
      const storyblok = StoryblokService.getObject();
      const params = getUrlParams();
      const isValidRedirect = typeof params.$redirect === 'string';

      if (storyblok) {
        storyblok.get({ slug: isValidRedirect ? (params.$redirect as string) : '/home' }, (...args) => {
          if (callback) {
            callback(...args);
          }
          resolve();
        });
      } else {
        window.location.href = isValidRedirect ? (params.$redirect as string) : '/';
        resolve();
      }
    });
  },

  buildCountryHomepages(globalDatasourceEntries: Record<string, GlobalContent>): Record<string, Homepage> {
    return Object.fromEntries(
      Object.entries(globalDatasourceEntries).map(([key, localisedGlobalContent]) => {
        const homepageByUserType = localisedGlobalContent.navigation.homepage;
        const cleanedHomepage = Object.fromEntries(
          Object.entries(homepageByUserType).filter(([, value]) => value !== '' && value !== '/' && !!value),
        );
        return [key, cleanedHomepage];
      }),
    );
  },

  buildPageAlternates({
    storyAlternates,
    currentDomain,
    globalDatasourceEntries,
    reporter,
  }: {
    storyAlternates: StoryblokAlternate[];
    currentDomain: Domain;
    globalDatasourceEntries: Record<string, GlobalContent>;
    reporter?: Reporter;
  }): Alternate[] {
    return storyAlternates
      .map((sbAlternate: StoryblokAlternate) => this.buildPageAlternate({ sbAlternate, currentDomain, globalDatasourceEntries, reporter }))
      .filter(Boolean);
  },

  buildPageAlternate({
    sbAlternate,
    currentDomain,
    globalDatasourceEntries,
    reporter,
  }: {
    sbAlternate: StoryblokAlternate;
    currentDomain: Domain;
    globalDatasourceEntries: Record<string, GlobalContent>;
    reporter?: Reporter;
  }): Alternate {
    const fullSlug = sbAlternate.full_slug || sbAlternate.fullSlug;
    if (!fullSlug) {
      return null;
    }

    const { country, locale, countryLocaleCode, localeCountryCode } = StringService.getCountryLocaleInfo(fullSlug);

    if (!countryLocaleCode) {
      if (reporter) {
        reporter.warn(`Alternate ${fullSlug} does not have valid country and locale info and therefore skipped`);
      }
      return null;
    }

    const url = DomainService.createDomainSpecificLink(fullSlug, currentDomain);

    const alternateDomain = StringService.isRelativeUrl(url) ? currentDomain : DomainService.getDomainByUrl(url);
    const { slug } = globalDatasourceEntries[countryLocaleCode];
    const userType = this.getPageUserType(fullSlug, alternateDomain, country, slug);

    return {
      id: sbAlternate.id,
      domain: alternateDomain,
      url,
      userType,
      country,
      locale,
      localeCountryCode,
    };
  },

  buildCountryLocalesMap(globalDatasourceEntries: Record<string, GlobalContent>): Record<CountryEnum, LocaleEnum[]> {
    // country configs are the same for all dimensions
    const defaultDatasourceEntries = Object.values(globalDatasourceEntries)[0];
    const { countryConfigs } = defaultDatasourceEntries;

    return Object.entries(countryConfigs).reduce(
      (acc, [country, config]) => {
        acc[country as CountryEnum] = config.locales.split(',') as LocaleEnum[];
        return acc;
      },
      { [CountryEnum.RCM]: [LocaleEnum.INT] } as Record<CountryEnum, LocaleEnum[]>, // rcm-int should be in the country locale map as a fallback
    );
  },
};
